import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, MapPin, Users, ChevronRight, ChevronLeft,
  CheckCircle2, Plus, X, Loader2, Image, Camera
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENUE_TYPES = [
  { id: 'event_space', name: 'Event Space', icon: '🏛' },
  { id: 'restaurant', name: 'Restaurant / Bar', icon: '🍽' },
  { id: 'rooftop', name: 'Rooftop', icon: '🌇' },
  { id: 'warehouse', name: 'Warehouse', icon: '🏭' },
  { id: 'gallery', name: 'Gallery / Studio', icon: '🎨' },
  { id: 'outdoor', name: 'Outdoor / Garden', icon: '🌿' },
  { id: 'hotel', name: 'Hotel / Ballroom', icon: '🏨' },
  { id: 'private_estate', name: 'Private Estate', icon: '🏡' },
  { id: 'brewery', name: 'Brewery / Winery', icon: '🍷' },
  { id: 'coworking', name: 'Coworking', icon: '💻' },
];

const AMENITIES = [
  'WiFi', 'Parking', 'Kitchen', 'Bar Area', 'Sound System', 'Stage',
  'Projector & Screen', 'Tables & Chairs', 'Outdoor Space', 'Restrooms',
  'A/V Equipment', 'Wheelchair Accessible', 'Elevator', 'Climate Control',
  'Lighting System', 'Dance Floor', 'Green Room', 'Loading Dock',
  'On-site Coordinator', 'Security', 'Coat Check'
];

const STEPS = [
  { id: 1, title: 'Basics', desc: 'Name & type' },
  { id: 2, title: 'Details', desc: 'Location & capacity' },
  { id: 3, title: 'Amenities', desc: 'What you offer' },
  { id: 4, title: 'Photos', desc: 'Show your space' },
];

export default function VenueOwnerOnboarding() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', venue_type: 'event_space',
    address: '', city: 'Denver', state: 'CO', zip_code: '',
    capacity_min: 10, capacity_max: 200,
    amenities: [], house_rules: [],
    cancellation_policy: 'flexible', photos: [], cover_photo: '',
  });
  const [newRule, setNewRule] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const toggleAmenity = (a) => {
    set('amenities', form.amenities.includes(a) ? form.amenities.filter(x => x !== a) : [...form.amenities, a]);
  };

  const addRule = () => {
    if (newRule.trim()) { set('house_rules', [...form.house_rules, newRule.trim()]); setNewRule(''); }
  };

  const addPhoto = () => {
    if (photoUrl.trim()) { set('photos', [...form.photos, photoUrl.trim()]); setPhotoUrl(''); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.address) { toast.error('Name and address required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/venues`, form, { headers });
      toast.success('Venue created! Setting up your dashboard...');
      setTimeout(() => navigate('/venue-dashboard'), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create venue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="venue-onboarding">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <Building2 className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">List Your Venue</h1>
          <p className="text-muted-foreground text-sm mt-1">Join Eventism's venue marketplace</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-1 mb-8" data-testid="onboarding-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button onClick={() => step > s.id && setStep(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${step === s.id ? 'bg-indigo-600 text-foreground' : step > s.id ? 'bg-indigo-500/20 text-indigo-400 cursor-pointer' : 'bg-muted text-muted-foreground'}`}>
                {step > s.id ? <CheckCircle2 className="h-3 w-3" /> : null}
                {s.title}
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </React.Fragment>
          ))}
        </div>

        <Card className="bg-muted/60 border-border">
          <CardContent className="p-6 space-y-5">
            {/* STEP 1: Basics */}
            {step === 1 && (
              <>
                <div>
                  <Label className="text-xs mb-2 block">Venue Name *</Label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} className="bg-muted border-border" placeholder="The Grand Ballroom at Mile High" data-testid="venue-name-input" />
                </div>
                <div>
                  <Label className="text-xs mb-2 block">Venue Type</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="venue-type-selector">
                    {VENUE_TYPES.map(t => (
                      <button key={t.id} onClick={() => set('venue_type', t.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${form.venue_type === t.id ? 'bg-indigo-500/20 border-indigo-500/40' : 'bg-muted/50 border-border hover:border-border'}`}>
                        <span className="text-lg">{t.icon}</span>
                        <p className={`text-xs font-medium mt-1 ${form.venue_type === t.id ? 'text-indigo-400' : 'text-muted-foreground'}`}>{t.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-2 block">Description</Label>
                  <Textarea value={form.description} onChange={e => set('description', e.target.value)} className="bg-muted border-border" rows={4} placeholder="Describe your venue, its atmosphere, and what makes it special..." data-testid="venue-description" />
                </div>
              </>
            )}

            {/* STEP 2: Details */}
            {step === 2 && (
              <>
                <div>
                  <Label className="text-xs mb-2 block">Street Address *</Label>
                  <Input value={form.address} onChange={e => set('address', e.target.value)} className="bg-muted border-border" placeholder="1234 Larimer St" data-testid="venue-address" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">City</Label><Input value={form.city} onChange={e => set('city', e.target.value)} className="bg-muted border-border" /></div>
                  <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => set('state', e.target.value)} className="bg-muted border-border" /></div>
                  <div><Label className="text-xs">ZIP</Label><Input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} className="bg-muted border-border" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Min Capacity</Label><Input type="number" value={form.capacity_min} onChange={e => set('capacity_min', parseInt(e.target.value) || 0)} className="bg-muted border-border" /></div>
                  <div><Label className="text-xs">Max Capacity</Label><Input type="number" value={form.capacity_max} onChange={e => set('capacity_max', parseInt(e.target.value) || 0)} className="bg-muted border-border" /></div>
                </div>
                <div>
                  <Label className="text-xs mb-2 block">Cancellation Policy</Label>
                  <div className="flex gap-2">
                    {['flexible', 'moderate', 'strict'].map(p => (
                      <button key={p} onClick={() => set('cancellation_policy', p)}
                        className={`px-4 py-2 rounded-lg text-xs font-medium capitalize transition-all ${form.cancellation_policy === p ? 'bg-indigo-600 text-foreground' : 'bg-muted text-muted-foreground border border-border'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-2 block">House Rules</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={newRule} onChange={e => setNewRule(e.target.value)} className="bg-muted border-border flex-1" placeholder="e.g., No outside catering allowed" onKeyDown={e => e.key === 'Enter' && addRule()} />
                    <Button size="sm" onClick={addRule} variant="outline"><Plus className="h-3 w-3" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {form.house_rules.map((r, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-muted rounded-full text-muted-foreground">
                        {r} <X className="h-2.5 w-2.5 cursor-pointer hover:text-red-400" onClick={() => set('house_rules', form.house_rules.filter((_, j) => j !== i))} />
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* STEP 3: Amenities */}
            {step === 3 && (
              <>
                <Label className="text-xs mb-2 block">Select all amenities your venue offers</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="amenities-grid">
                  {AMENITIES.map(a => (
                    <button key={a} onClick={() => toggleAmenity(a)}
                      className={`p-2.5 rounded-lg border text-xs text-left transition-all ${form.amenities.includes(a) ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-400' : 'bg-muted/50 border-border text-muted-foreground hover:border-border'}`}>
                      {form.amenities.includes(a) && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
                      {a}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* STEP 4: Photos */}
            {step === 4 && (
              <>
                <Label className="text-xs mb-2 block">Add venue photos (paste image URLs)</Label>
                <div className="flex gap-2 mb-3">
                  <Input value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} className="bg-muted border-border flex-1" placeholder="https://images.unsplash.com/..." onKeyDown={e => e.key === 'Enter' && addPhoto()} data-testid="photo-url-input" />
                  <Button size="sm" onClick={addPhoto} variant="outline"><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="photo-grid">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative group">
                      <img src={p} alt="" className="w-full h-24 object-cover rounded-lg" />
                      <button onClick={() => set('photos', form.photos.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 bg-red-500/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-3 w-3 text-foreground" />
                      </button>
                      {i === 0 && <span className="absolute bottom-1 left-1 text-[8px] bg-indigo-600 text-foreground px-1.5 py-0.5 rounded">Cover</span>}
                    </div>
                  ))}
                  {form.photos.length === 0 && (
                    <div className="col-span-3 py-8 text-center">
                      <Camera className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">Add photos to make your listing shine</p>
                    </div>
                  )}
                </div>
                {form.photos.length > 0 && (
                  <div>
                    <Label className="text-xs mb-1 block">Cover Photo URL (or first photo used)</Label>
                    <Input value={form.cover_photo} onChange={e => set('cover_photo', e.target.value)} className="bg-muted border-border" placeholder="Auto-uses first photo" />
                  </div>
                )}
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-3 border-t border-border">
              {step > 1 ? (
                <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} className="text-muted-foreground">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              ) : <div />}
              {step < 4 ? (
                <Button onClick={() => setStep(s => s + 1)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="next-step-btn">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold" data-testid="create-venue-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Submit for Review
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
